// src/lib/firebase.ts
import { initializeApp, getApps } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { monitor } from './monitoring';

// ================================
// 🔧 Firebase Configuration
// ================================
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// ================================
// 🚀 Initialize Firebase
// ================================
const apps = getApps();
const app = apps.length ? apps[0] : initializeApp(firebaseConfig);

// ================================
// 🛡️ Initialize App Check (reCAPTCHA v3)
// ================================
// Enable App Check debug mode in development so localhost reads aren't blocked.
// The debug token is printed to the browser console on first run — register it
// in the Firebase Console under App Check → Apps → Manage debug tokens.
if (import.meta.env.DEV) {
  (globalThis as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
}

// IMPORTANT: this must match your .env.local variable name
const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY;

const appCheck: ReturnType<typeof initializeAppCheck> | null = (() => {
  // Only run App Check in the browser (not SSR)
  if (typeof window !== 'undefined' && siteKey) {
    try {
      const instance = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      monitor.info('App Check active (reCAPTCHA v3)');
      return instance;
    } catch (err) {
      monitor.error('Failed to initialize App Check', { error: err });
      return null;
    }
  }

  monitor.warn('App Check skipped (SSR or missing key)');
  return null;
})();

// ================================
// 🔥 Firebase Services
// ================================
// Detect Vitest without relying on Node's `process` types
const IS_TEST = Boolean(
  (import.meta as any)?.vitest || (import.meta as any)?.env?.MODE === 'test',
);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// When running tests, point SDKs at local emulators instead of exporting dummies
if (IS_TEST) {
  const isEmulatorUnavailable = (err: unknown) => {
    const msg = (err as any)?.message || '';
    const name = (err as any)?.name || '';
    const code = (err as any)?.code || '';
    return /ECONNREFUSED|ENOTFOUND|Failed to fetch|NetworkError|connection|unavailable/i.test(
      String(msg || name || code),
    );
  };

  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    monitor.debug('Connected Auth emulator');
  } catch (err) {
    if (isEmulatorUnavailable(err)) {
      monitor.warn('Auth emulator not running; continuing without emulator');
    } else {
      monitor.error('Failed to connect Auth emulator', { error: err });
    }
  }

  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    monitor.debug('Connected Firestore emulator');
  } catch (err) {
    if (isEmulatorUnavailable(err)) {
      monitor.warn('Firestore emulator not running; continuing without emulator');
    } else {
      monitor.error('Failed to connect Firestore emulator', { error: err });
    }
  }

  try {
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    monitor.debug('Connected Storage emulator');
  } catch (err) {
    if (isEmulatorUnavailable(err)) {
      monitor.warn('Storage emulator not running; continuing without emulator');
    } else {
      monitor.error('Failed to connect Storage emulator', { error: err });
    }
  }
}

// ================================
// 📤 Exports
// ================================
export { app, appCheck };
